/**
 * @pryme/sdk — server-side relying-party SDK.
 *
 *   const pryme = createPryme({ serviceId: process.env.PRYME_SERVICE_ID, secretKey: process.env.PRYME_SECRET_KEY, redirectUri });
 *   // browser login (Authorization Code + PKCE S256)
 *   const { url, transaction } = pryme.beginLogin({ claims: ['age.over18'] });
 *   const { identity } = await pryme.completeLogin(callbackQuery, transaction);
 *   // any request: browser session or signed agent request
 *   const id = await pryme.verify(request, { session });
 *   if (!id.isGenuine()) return id.reject();
 *   if (!id.isOver18()) return id.rejectWithMessage(undefined, 'Not old enough');
 *   if (id.isDelegated() && !(await id.authorize('commerce.purchase', { amount: 2499, currency: 'DKK', orderId }))) return id.reject();
 *
 * Delegated authority (ADR-0015): `authorize()` forwards the agent's signed intent for the exact
 * operation. Without one, Pryme treats the check as your own request: it may ask the person, and the
 * agent's permission never allows it by itself.
 *
 * Trust (ADR-0014):
 *  - Service channel: your secret key carries Pryme's verification key for its own generation (or give it
 *    as `prymeKeys`). Every response from Pryme is checked against it. Never fetched at runtime.
 *  - Identity bindings (pairwise key → verified, active Pryme identity for your service) are signed by
 *    Pryme's attestation issuer. Its key set arrives only in a response verified as above, is cached for
 *    at most 10 minutes and refreshed on an unknown key id, so it rotates without your involvement.
 *  A random public key presented by a client is never trusted (T15).
 * The secret key (sk_test_… / sk_live_…) MUST stay on your backend.
 */
import { HEADERS, REQUEST_MAX_AGE_SECONDS, TYP } from '@pryme/protocol';
import { b64url, contentDigest, ed25519, isoSeconds, jwsVerify, randomB64, sha256b64, signatureBase, utf8 } from './crypto.js';
import { PrymeSdkError } from './errors.js';
import { verifyCheckoutPresentation } from './ap2.js';
import { PrymeIdentity } from './identity.js';
export * from './crypto.js';
export { PrymeSdkError } from './errors.js';
export { createAgent, pairAgent, pollPairing, resolveAgentConfig, verifyWebhook } from './agent.js';
import { isSecretKey, parseServiceSecret } from './secret.js';
export { encodeAgentSecret, encodeServiceSecret, isSecretKey, parseAgentSecret, parseServiceSecret, secretKeyEnvironment } from './secret.js';
export * as ap2 from './ap2.js';
export { PrymeIdentity } from './identity.js';
export { AP2_ERROR_FOR, AUTHORIZATION_DETAILS_TYPE, mandateAuthorizationDetails } from '@pryme/protocol';
export class MemoryStateStore {
    m = new Map();
    get(key) {
        const e = this.m.get(key);
        if (!e || e.exp < Date.now())
            return undefined;
        return e.v;
    }
    set(key, v, ttl) {
        if (this.m.size > 50_000)
            this.m.clear();
        this.m.set(key, { v, exp: Date.now() + ttl * 1000 });
    }
}
export class MemoryReplayStore {
    m = new Map();
    consume(key, ttl) {
        const now = Date.now();
        if (this.m.size > 100_000)
            for (const [k, exp] of this.m)
                if (exp < now)
                    this.m.delete(k);
        const exp = this.m.get(key);
        if (exp && exp > now)
            return false;
        this.m.set(key, now + ttl * 1000);
        return true;
    }
}
/**
 * Converts a fetch `Request`. Call `pryme.verify()` BEFORE your framework consumes the body
 * (e.g. Hono `c.req.json()`/`parseBody()`), because the signature covers the raw body bytes.
 */
export async function fromFetchRequest(req) {
    const url = new URL(req.url);
    if (req.bodyUsed)
        throw new PrymeSdkError('invalid_request', 'request body already consumed: call pryme.verify(request) before reading the body');
    const body = req.method === 'GET' || req.method === 'HEAD' ? new Uint8Array() : new Uint8Array(await req.clone().arrayBuffer());
    return { method: req.method.toUpperCase(), target: url.pathname + url.search, header: (n) => req.headers.get(n) ?? undefined, body };
}
const S256 = (verifier) => sha256b64(verifier);
/**
 * A fetched attestation key set is trusted for at most this long, then refreshed before use: the bound on
 * how long a verifier keeps trusting an issuer key Pryme has revoked in an emergency.
 */
export const ISSUER_KEYS_MAX_AGE_MS = 10 * 60_000;
/**
 * An unknown key id in a binding a client presented (agent request, stored session) triggers a refresh at
 * most this often, so forged ids cannot flood Pryme. A binding Pryme itself just returned over the signed
 * channel (sign-in, refresh) refreshes at once: after an issuer rotation it carries the new key id.
 */
export const ISSUER_KEYS_MIN_REFRESH_MS = 5_000;
const RAW_KEY = /^[A-Za-z0-9_-]{43}$/;
/** Fill the configuration from the service secret key, refusing anything contradictory. */
export function resolveConfig(input) {
    const bad = (m) => {
        throw new PrymeSdkError('invalid_request', `Pryme configuration: ${m}`);
    };
    let fromSecret = null;
    if (input.secretKey !== undefined) {
        if (!isSecretKey(input.secretKey))
            bad('secretKey must be the sk_test_… / sk_live_… value from the credentials panel (for a raw signing key use signingKey)');
        try {
            fromSecret = parseServiceSecret(input.secretKey);
        }
        catch (e) {
            bad(e.message);
        }
        if (fromSecret.serviceId !== input.serviceId)
            bad(`secretKey belongs to ${fromSecret.serviceId}, not ${input.serviceId}`);
        if (input.environment && input.environment !== fromSecret.environment)
            bad(`secretKey is a ${fromSecret.environment} key but environment is ${input.environment}`);
    }
    const apiBase = input.apiBase ?? fromSecret?.apiBase ?? bad('apiBase or secretKey is required');
    const apiAudience = input.apiAudience ?? fromSecret?.apiAudience ?? bad('apiAudience or secretKey is required');
    const signingKey = input.signingKey ?? fromSecret?.signingKey ?? bad('secretKey (or signingKey) is required');
    const prymeKeys = input.prymeKeys ?? fromSecret?.prymeKeys ?? bad('secretKey (or prymeKeys) is required');
    if (!prymeKeys.length)
        bad('at least one Pryme verification key is required');
    const issuer = input.issuer ?? fromSecret?.issuer ?? apiBase;
    return { ...input, apiBase, apiAudience, issuer, signingKey, prymeKeys, environment: input.environment ?? fromSecret?.environment ?? 'sandbox' };
}
export function createPryme(input) {
    const cfg = resolveConfig(input);
    const doFetch = cfg.fetch ?? ((...a) => fetch(...a));
    const now = cfg.now ?? (() => Date.now());
    const nowS = () => Math.floor(now() / 1000);
    const stateStore = cfg.stateStore ?? new MemoryStateStore();
    const replay = cfg.replayStore ?? new MemoryReplayStore();
    const expectedEnvironment = cfg.environment;
    const emit = (type, detail) => cfg.onEvent?.({ type, detail });
    const apiBase = cfg.apiBase.replace(/\/$/, '');
    // Browser-facing sign-in lives at the issuer; server-to-server calls go to the API base.
    const issuer = cfg.issuer.replace(/\/$/, '');
    function prymeKey(kid) {
        const exact = cfg.prymeKeys.filter((k) => k.keyId === kid);
        return exact.length ? exact : [];
    }
    // ---- Pryme's attestation issuer keys (ADR-0014) -------------------------------------------------
    let issuerKeySet = null;
    let lastIssuerFetch = Number.NEGATIVE_INFINITY;
    let issuerFetch = null;
    async function loadIssuerKeys() {
        lastIssuerFetch = now();
        // A signed call: the key set is accepted only in a response verified with this secret's Service A key.
        const doc = await call('/v1/issuer/keys', {});
        if ((doc.issuer ?? '').replace(/\/$/, '') !== issuer)
            throw new PrymeSdkError('issuer_not_verified', 'attestation keys from a different issuer');
        if (doc.environment !== expectedEnvironment) {
            throw new PrymeSdkError('issuer_not_verified', `this verifier expects ${expectedEnvironment}; Pryme answered with ${doc.environment} issuer keys`);
        }
        const keys = (Array.isArray(doc.keys) ? doc.keys : [])
            .filter((k) => k && k.kty === 'OKP' && k.crv === 'Ed25519' && k.alg === 'EdDSA' && typeof k.kid === 'string' && typeof k.x === 'string' && RAW_KEY.test(k.x))
            .map((k) => ({ keyId: k.kid, publicKey: k.x }));
        issuerKeySet = { keys, at: now() };
        emit('issuer_keys.refreshed', { keys: keys.length });
    }
    /**
     * The trusted attestation key with this id, if any. The cached set is used while younger than
     * ISSUER_KEYS_MAX_AGE_MS; an unknown id refreshes it (rate-limited unless `fromPryme`). Past its age it
     * must be refreshed: if Pryme cannot be reached, nothing verifies (fail closed).
     */
    async function attestationKey(kid, fromPryme) {
        const isFresh = () => !!issuerKeySet && now() - issuerKeySet.at < ISSUER_KEYS_MAX_AGE_MS;
        const find = () => (isFresh() ? issuerKeySet.keys.find((k) => k.keyId === kid) : undefined);
        const known = find();
        if (known)
            return known;
        if (!isFresh() || fromPryme || now() - lastIssuerFetch >= ISSUER_KEYS_MIN_REFRESH_MS) {
            issuerFetch ??= loadIssuerKeys().finally(() => {
                issuerFetch = null;
            });
            try {
                await issuerFetch;
            }
            catch (e) {
                if (!isFresh())
                    throw e;
            }
        }
        return find();
    }
    /**
     * Verify an identity binding: one `pryme-binding-v2` JWS by Pryme's attestation issuer, for this
     * service (audience), this issuer and this trust environment, unexpired. `fromPryme`: the binding came
     * in a response verified with this secret's Service A key, not from a client.
     */
    async function verifyBinding(token, fromPryme = false) {
        const bad = (code, message) => ({ ok: false, code, message });
        if (!token || token.includes(','))
            return bad('issuer_not_verified', 'expected exactly one identity binding');
        let kid;
        try {
            kid = String(JSON.parse(new TextDecoder().decode(b64url.decode(token.split('.')[0] ?? ''))).kid ?? '');
        }
        catch {
            return bad('issuer_not_verified', 'malformed identity binding');
        }
        let key;
        try {
            key = await attestationKey(kid, fromPryme);
        }
        catch (e) {
            return bad(e.code ?? 'temporarily_unavailable', `attestation keys unavailable: ${e.message}`);
        }
        const v = key ? jwsVerify(key.publicKey, token, TYP.binding) : null;
        if (!v)
            return bad('issuer_not_verified', 'identity binding not signed by a trusted Pryme attestation key');
        const p = v.payload;
        if (p.v !== 2 || (p.iss ?? '').replace(/\/$/, '') !== issuer)
            return bad('issuer_not_verified', 'identity binding from a different issuer');
        if (p.aud !== cfg.serviceId)
            return bad('wrong_audience', 'identity binding issued for another service');
        if (p.environment !== expectedEnvironment)
            return bad('issuer_not_verified', 'identity binding from another trust environment');
        if (p.exp <= nowS())
            return bad('expired_presentation', 'identity binding expired');
        if (p.identity_status !== 'active' || typeof p.pairwise_key !== 'string' || `pks_${p.pairwise_key}` !== p.subject)
            return bad('issuer_not_verified', 'identity binding is malformed');
        return { ok: true, payload: p };
    }
    function fail(kind, code, message) {
        emit('verify.rejected', { code });
        return new PrymeIdentity({ kind, serviceId: cfg.serviceId, expectedEnvironment, now, error: { code, message } });
    }
    /** Verification of {state, binding}: local, except a first or periodic fetch of the attestation key set. */
    async function identityFromSession(session, nonce, source = 'local') {
        if (!session.state || !session.binding)
            return fail('session', 'consent_required', 'no Pryme state in session');
        const checked = await verifyBinding(session.binding, source !== 'local');
        if (!checked.ok)
            return fail('session', checked.code, checked.message);
        const binding = checked.payload;
        if (binding.subject !== session.subject)
            return fail('session', 'wrong_audience', 'subject mismatch');
        const v = jwsVerify(binding.pairwise_key, session.state, TYP.state);
        if (!v || v.header.kid !== binding.pairwise_kid)
            return fail('session', 'invalid_signature', 'state signature invalid');
        const s = v.payload;
        if (s.aud !== cfg.serviceId || s.service_id !== cfg.serviceId || s.subject !== binding.subject) {
            return fail('session', 'wrong_audience', 'state not issued for this service');
        }
        if (nonce !== undefined && s.nonce !== nonce)
            return fail('session', 'invalid_signature', 'nonce mismatch');
        const maxAge = cfg.maxStateAgeSeconds;
        const effective = maxAge ? { ...s, exp: Math.min(s.exp, s.iat + maxAge) } : s;
        emit('state.verified', { cacheHit: source === 'local', source });
        return new PrymeIdentity({
            kind: 'session', serviceId: cfg.serviceId, expectedEnvironment, now, binding, state: effective,
            stateToken: session.state, bindingToken: session.binding,
        });
    }
    /** Signed service → Pryme call with verified Service-A response signature. */
    async function call(path, body, method = 'POST') {
        const text = body === undefined ? '' : JSON.stringify(body);
        const url = new URL(path, apiBase + '/');
        const nonce = randomB64(18);
        const timestamp = isoSeconds(now());
        const digest = contentDigest(text);
        const base = signatureBase({
            principal: `service:${cfg.serviceId}`, keyId: cfg.signingKey.keyId, method, audience: cfg.apiAudience,
            target: url.pathname + url.search, contentDigest: digest, timestamp, nonce,
        });
        let res;
        try {
            res = await doFetch(url.toString(), {
                method,
                headers: {
                    'content-type': 'application/json',
                    [HEADERS.service]: cfg.serviceId,
                    [HEADERS.keyId]: cfg.signingKey.keyId,
                    [HEADERS.timestamp]: timestamp,
                    [HEADERS.nonce]: nonce,
                    [HEADERS.digest]: digest,
                    [HEADERS.signature]: `ed25519=${ed25519.sign(cfg.signingKey.secretKey, utf8(base))}`,
                },
                body: method === 'GET' ? undefined : text,
            });
        }
        catch (e) {
            throw new PrymeSdkError('temporarily_unavailable', `Pryme unreachable: ${e.message}`);
        }
        const resText = await res.text();
        if (!res.ok) {
            let code = 'temporarily_unavailable';
            let message = `HTTP ${res.status}`;
            try {
                const j = JSON.parse(resText);
                code = j.error?.code ?? code;
                message = j.error?.message ?? message;
            }
            catch { /* non-JSON error */ }
            throw new PrymeSdkError(code, message, res.status);
        }
        const sig = res.headers.get(HEADERS.responseSignature);
        let kid;
        try {
            kid = JSON.parse(new TextDecoder().decode(b64url.decode((sig ?? '').split('.')[0] ?? ''))).kid;
        }
        catch { /* handled below */ }
        const verified = sig ? prymeKey(kid).map((k) => jwsVerify(k.publicKey, sig, TYP.response)).find(Boolean) : null;
        const p = verified?.payload;
        if (!p || p.service_id !== cfg.serviceId || p.request_nonce !== nonce || p.body_digest !== contentDigest(resText) || p.status !== res.status) {
            emit('response.signature_invalid');
            throw new PrymeSdkError('invalid_response', 'Pryme response signature missing or invalid');
        }
        emit('response.signature_verified', { path: url.pathname });
        return JSON.parse(resText);
    }
    /** `claims` undefined = the claim set configured for this service in the developer portal. */
    function cacheKey(subject, claims) {
        // Shared by every deployment of the service, whatever secret key it runs on: state and binding do
        // not depend on the service's credentials (ADR-0014).
        return ['v3', cfg.serviceId, subject, cfg.serviceId, claims ? [...claims].sort().join(',') : '<service-default>', expectedEnvironment].join('|');
    }
    async function refresh(subject, claims = cfg.claims) {
        const r = await call('/v1/verify', { subject, requestedClaims: claims, audience: cfg.serviceId });
        const session = { subject: r.subject, state: r.state, binding: r.binding };
        const id = await identityFromSession(session, undefined, 'refresh');
        if (id.isGenuine()) {
            const ttl = Math.max(1, Math.floor(((id.expiresAt?.getTime() ?? 0) - now()) / 1000));
            // Cache key covers service, subject, audience, claims; the state itself pins policy epoch and kid.
            await stateStore.set(cacheKey(subject, claims), session, ttl);
        }
        emit('state.refreshed', { genuine: id.isGenuine() });
        return id;
    }
    async function authorizeOnline(binding, mandate, agent, action, context) {
        const r = await call('/v1/authorizations/check', {
            subject: binding.subject, mandateId: mandate.mandate_id, agentId: agent.agentId, approvalId: agent.approvalId,
            agentIntent: agent.intent, context: { ...context, action },
        });
        if (!r.decision)
            return { outcome: 'deny', code: r.code ?? 'unknown_pairwise_identity', reason: r.reason };
        const v = jwsVerify(binding.pairwise_key, r.decision, TYP.decision);
        const d = v?.payload;
        if (!d || d.aud !== cfg.serviceId || d.subject !== binding.subject || d.mandate_id !== mandate.mandate_id || d.agent_id !== agent.agentId) {
            throw new PrymeSdkError('invalid_signature', 'decision not signed by the human\'s pairwise key for this service');
        }
        emit('authorization.decision', { outcome: d.outcome });
        return {
            outcome: d.outcome, code: d.code, reason: d.reason, authorizationId: d.authorization_id, approvalId: d.approval_id,
            approvalExpiresAt: d.approval_expires_at, operationHash: d.operation_hash, reserved: d.reserved, token: r.decision, idempotent: r.idempotent,
        };
    }
    /** Verify an agent's signed request + Pryme mandate (spec §7.2). */
    async function verifyAgentRequest(req) {
        const h = (n) => req.header(n) ?? '';
        const agentId = h(HEADERS.agent);
        const keyId = h(HEADERS.keyId);
        const ts = h(HEADERS.timestamp);
        const nonce = h(HEADERS.nonce);
        const digest = h(HEADERS.digest);
        const sig = h(HEADERS.signature);
        const audience = h(HEADERS.audience);
        if (!agentId || !keyId || !ts || !nonce || !digest || !sig.startsWith('ed25519='))
            return fail('agent', 'invalid_signature', 'missing agent signature headers');
        if (audience !== cfg.serviceId)
            return fail('agent', 'wrong_audience', 'request not addressed to this service');
        const checked = await verifyBinding(h(HEADERS.binding));
        if (!checked.ok)
            return fail('agent', checked.code, checked.message);
        const binding = checked.payload;
        const mv = jwsVerify(binding.pairwise_key, h(HEADERS.mandate), TYP.mandate);
        if (!mv || mv.header.kid !== binding.pairwise_kid)
            return fail('agent', 'invalid_signature', 'mandate not signed by the human\'s pairwise key');
        const m = mv.payload;
        if (m.aud !== cfg.serviceId || m.subject !== binding.subject)
            return fail('agent', 'wrong_audience', 'mandate issued for another recipient');
        if (m.agent_id !== agentId || m.agent_kid !== keyId)
            return fail('agent', 'agent_not_authorized', 'mandate not issued to this agent key');
        if (m.exp <= nowS() || Date.parse(m.expires_at) <= now())
            return fail('agent', 'mandate_expired', 'mandate proof expired');
        if (Date.parse(m.effective_at) > now())
            return fail('agent', 'constraint_violation', 'mandate not yet effective');
        const t = Date.parse(ts);
        if (!Number.isFinite(t) || Math.abs(now() - t) > REQUEST_MAX_AGE_SECONDS * 1000)
            return fail('agent', 'expired_presentation', 'request timestamp outside window');
        if (contentDigest(req.body) !== digest)
            return fail('agent', 'invalid_signature', 'body digest mismatch');
        const base = (() => {
            try {
                return signatureBase({ principal: `agent:${agentId}`, keyId, method: req.method, audience, target: req.target, contentDigest: digest, timestamp: ts, nonce });
            }
            catch {
                return null;
            }
        })();
        if (!base || !ed25519.verify(m.agent_key, utf8(base), sig.slice('ed25519='.length)))
            return fail('agent', 'invalid_signature', 'agent request signature invalid');
        // The agent's signed intent for this request (optional): same key, recipient, mandate and nonce.
        // Present means it must verify: an empty or invalid intent fails the request, never counts as absent.
        const rawIntent = req.header(HEADERS.intent);
        const intent = rawIntent === undefined || rawIntent === null ? undefined : rawIntent;
        if (intent !== undefined) {
            const iv = jwsVerify(m.agent_key, intent, TYP.intent);
            const ip = iv?.payload;
            if (!iv || iv.header.kid !== keyId || !ip || ip.v !== 1 || ip.agent_id !== agentId || ip.aud !== cfg.serviceId || ip.mandate_id !== m.mandate_id || ip.nonce !== nonce) {
                return fail('agent', 'invalid_signature', 'Pryme-Intent is not this agent\'s intent for this request');
            }
            if (!Number.isSafeInteger(ip.iat) || !Number.isSafeInteger(ip.exp) || ip.exp <= nowS() || ip.exp - ip.iat > REQUEST_MAX_AGE_SECONDS)
                return fail('agent', 'expired_presentation', 'agent intent expired');
        }
        if (!(await replay.consume(`${agentId}|${nonce}`, REQUEST_MAX_AGE_SECONDS * 2)))
            return fail('agent', 'replay_detected', 'request nonce already seen');
        const agent = { agentId, nonce, approvalId: h(HEADERS.approval) || undefined, intent };
        emit('agent.verified', { agentId });
        return new PrymeIdentity({
            kind: 'agent', serviceId: cfg.serviceId, expectedEnvironment, now, binding, mandate: m, agent,
            authorizeFn: (action, ctx) => authorizeOnline(binding, m, agent, action, ctx),
        });
    }
    return {
        config: { serviceId: cfg.serviceId, apiBase, environment: expectedEnvironment },
        beginLogin(opts = {}) {
            const redirectUri = opts.redirectUri ?? cfg.redirectUri;
            if (!redirectUri)
                throw new Error('redirectUri required');
            const claims = opts.claims ?? cfg.claims ?? ['human.verified'];
            const transaction = {
                state: randomB64(24), nonce: randomB64(24), codeVerifier: randomB64(48), redirectUri, claims, createdAt: now(),
            };
            const u = new URL('/authorize', issuer + '/');
            u.search = new URLSearchParams({
                response_type: 'code', client_id: cfg.serviceId, redirect_uri: redirectUri, scope: 'openid',
                claims: claims.join(' '), state: transaction.state, nonce: transaction.nonce,
                code_challenge: S256(transaction.codeVerifier), code_challenge_method: 'S256',
            }).toString();
            return { url: u.toString(), transaction };
        },
        /** Validate the callback against the stored transaction and exchange the code (signed). */
        async completeLogin(query, transaction) {
            const q = query instanceof URLSearchParams ? query : new URLSearchParams(query);
            if (!transaction)
                throw new PrymeSdkError('invalid_request', 'no login transaction for this browser session');
            if (now() - transaction.createdAt > 10 * 60_000)
                throw new PrymeSdkError('expired_presentation', 'login transaction expired');
            if (q.get('state') !== transaction.state)
                throw new PrymeSdkError('invalid_request', 'state mismatch (possible CSRF or injection)');
            const iss = q.get('iss');
            if (iss && iss.replace(/\/$/, '') !== issuer)
                throw new PrymeSdkError('issuer_not_verified', 'issuer mix-up detected');
            if (q.get('error'))
                throw new PrymeSdkError('consent_required', q.get('error_description') ?? q.get('error') ?? 'access denied');
            const code = q.get('code');
            if (!code)
                throw new PrymeSdkError('invalid_request', 'missing code');
            const r = await call('/token', {
                grant_type: 'authorization_code', code, code_verifier: transaction.codeVerifier, redirect_uri: transaction.redirectUri, client_id: cfg.serviceId,
            });
            const session = { subject: r.subject, state: r.state, binding: r.binding };
            const identity = await identityFromSession(session, transaction.nonce, 'login');
            if (!identity.isGenuine())
                throw new PrymeSdkError(identity.error.code, identity.error.message);
            await stateStore.set(cacheKey(r.subject, transaction.claims), session, Math.max(1, Math.floor(((identity.expiresAt?.getTime() ?? 0) - now()) / 1000)));
            return { identity, session };
        },
        identityFromSession,
        refresh,
        /**
         * Verify a request. Signed agent requests are detected by the `Pryme-Agent` header;
         * otherwise the merchant passes its own session's Pryme data. Fresh state is local;
         * missing/stale state is refreshed online.
         */
        async verify(req, opts = {}) {
            const incoming = typeof req.clone === 'function' ? await fromFetchRequest(req) : req;
            if (incoming.header(HEADERS.agent))
                return verifyAgentRequest(incoming);
            const s = opts.session;
            if (!s?.subject)
                return fail('none', 'identity_not_verified', 'no Pryme session or signed agent request');
            const claims = opts.claims ?? cfg.claims;
            const candidate = s.state && s.binding ? s : await stateStore.get(cacheKey(s.subject, claims));
            if (candidate?.state) {
                const local = await identityFromSession({ ...candidate, subject: s.subject });
                if (local.isGenuine() && !local.isStale())
                    return local;
            }
            try {
                return await refresh(s.subject, claims);
            }
            catch (e) {
                const err = e;
                return fail('session', err.code ?? 'temporarily_unavailable', err.message);
            }
        },
        async settle(identity, authorizationId, outcome) {
            if (!identity.subject)
                throw new PrymeSdkError('identity_not_verified');
            return call('/v1/authorizations/' + encodeURIComponent(authorizationId) + '/settle', { subject: identity.subject, outcome });
        },
        /** Read-only status of an escalated approval (`pending` | `approved` | `rejected` | `expired` | `consumed`). */
        approvalStatus(subject, approvalId) {
            return call('/v1/approvals/status', { subject, approvalId });
        },
        /** AP2 (docs/INTEROP.md §2): accept Pryme-issued AP2 checkout mandates. */
        ap2: (() => {
            let issuerCache = null;
            const issuerDoc = async (fresh = false) => {
                if (!fresh && issuerCache && now() - issuerCache.at < 300_000)
                    return issuerCache.doc;
                // Service-signed call: the response is signed with this service's Service A key (the trust anchor).
                const doc = await call('/v1/ap2/issuer', {});
                issuerCache = { doc, at: now() };
                return doc;
            };
            return {
                /**
                 * Enable AP2 for this service. `merchantId` is your AP2 `merchant.id` (default: your service id);
                 * `checkoutKeys` (EC P-256 JWKs with kid) let Pryme verify checkouts you sign (checkout-first issuance).
                 */
                registerMerchant: (args) => call('/v1/ap2/merchant', args),
                /** Pryme's AP2 issuer keys, fetched over the signed channel and cached for 5 minutes. */
                issuer: issuerDoc,
                /**
                 * Verify an agent's AP2 checkout presentation against the checkout this merchant issued.
                 * Adds Pryme checks on top of plain AP2: issuer = this Pryme; when the SDK is configured for
                 * production, sandbox issuance and merchant ids not authorized by a verified domain are rejected.
                 */
                async verifyCheckout(args) {
                    let doc = await issuerDoc();
                    let v = await verifyCheckoutPresentation({ ...args, issuerKeys: doc.keys, now: Math.floor(now() / 1000) });
                    if (!v.ok && v.reason === 'unknown issuer key id') {
                        doc = await issuerDoc(true); // key rotation
                        v = await verifyCheckoutPresentation({ ...args, issuerKeys: doc.keys, now: Math.floor(now() / 1000) });
                    }
                    if (!v.ok)
                        return v;
                    if (v.issuerClaims.iss !== doc.issuer)
                        return { ok: false, error: 'invalid_credential', reason: 'issued by a different issuer' };
                    const p = (v.issuerClaims.pryme ?? {});
                    if (expectedEnvironment === 'production' && p.environment !== 'production')
                        return { ok: false, error: 'invalid_credential', reason: 'sandbox mandate presented to a production merchant' };
                    if (expectedEnvironment === 'production' && p.merchant_binding?.status !== 'verified')
                        return { ok: false, error: 'invalid_credential', reason: 'merchant id not authorized by a verified domain' };
                    return { ...v, pryme: { subject: p.subject, authorizationId: p.authorization_id, mandateId: p.mandate_id, environment: p.environment, merchantBinding: p.merchant_binding } };
                },
                /** Report the outcome of the budget reservation Pryme made when it issued the mandate. */
                settle(pryme, outcome) {
                    if (!pryme.subject || !pryme.authorizationId)
                        throw new PrymeSdkError('invalid_request', 'not a Pryme-issued AP2 mandate');
                    return call('/v1/authorizations/' + encodeURIComponent(pryme.authorizationId) + '/settle', { subject: pryme.subject, outcome });
                },
            };
        })(),
        /** Activate a pending service by signing the dashboard's activation challenge with pair B. */
        activate(challenge) {
            return call('/v1/services/activate', { challenge });
        },
        /** Low-level signed call (e.g. playground echo). */
        call,
    };
}
